import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Layout as ReactGridLayout } from 'react-grid-layout';
import { colors } from '@dexma/ui-components';
import Layout, { LayoutType, LayoutElement, ConstraintsPerLayoutElement, LayoutElementType } from 'domain/entity/layout';
import { GridLayouts, GridLayoutElement } from 'adapter/in/dashboards/components/generic/ReactGrid';
import type { Breakpoint } from 'adapter/in/dashboards/components/Grid';
import { BREAKPOINTS, CELL_MARGIN, COLUMNS } from 'adapter/in/dashboards/components/Grid/constants';
import i18n from 'asset/translation';
import { elementFitsContraints } from 'adapter/in/dashboards/containers/Grid';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import { ALERT_ACTION_TYPE, dispatchAlertToaster, useAlertToaster } from 'adapter/in/common/context/Alert';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { isTablet, isDesktop, isSmartTV } from 'react-device-detect';
import { dispatchLayoutsView, useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { computeLayoutRows } from 'adapter/in/dashboards/components/generic/ReactGrid/utils';
import {
    buildBreakpoint,
    calculateBreakpointDesktop,
    calculateCellSize,
    generateGridBackground,
    withDashboardFrame,
} from 'adapter/in/dashboards/containers/Grid/utils';
import { mapGridElementsToLayout } from 'adapter/in/dashboards/components/Grid/gridUtils';
import {
    getWidgetCreationCoordinates,
    NewWidgetType,
    WidgetCoordinates,
} from 'adapter/in/dashboards/components/Grid/newWidgetOverlayUtils';
import { createLocalTextBlockGridElement } from 'adapter/in/dashboards/components/Grid/localTextBlock';

export const areGridLayoutsEqual = (layoutA: ReactGridLayout[], layoutB: ReactGridLayout[]): boolean => {
    const layoutBMap = new Map(layoutB.map((widgetLayout) => [widgetLayout.i, widgetLayout]));

    const isLayoutAaLayoutBSubset = layoutA.every((widgetLayoutA) => {
        const widgetLayoutB = layoutBMap.get(widgetLayoutA.i);

        if (widgetLayoutB) {
            if (
                widgetLayoutB.i === widgetLayoutA.i &&
                widgetLayoutB.w === widgetLayoutA.w &&
                widgetLayoutB.h === widgetLayoutA.h &&
                widgetLayoutB.x === widgetLayoutA.x &&
                widgetLayoutB.y === widgetLayoutA.y
            ) {
                return true;
            }
            return false;
        }
        return false;
    });

    return isLayoutAaLayoutBSubset && layoutA.length === layoutB.length;
};

const getComparableLayout = (layout: ReactGridLayout[]): ReactGridLayout[] =>
    layout.filter((element) => !isDashboardFrame(element.i, (element as GridLayoutElement).type));

export type WidgetBeingResized = GridLayoutElement & { fitsInGrid: boolean };
export const useGridView = (
    gridLayouts: GridLayouts,
    allGridLayouts: GridLayouts,
    editingLayout: boolean,
    selectedLayout: LayoutType,
    isEditEnabled: boolean,
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>
): {
    cellSize: {
        width: number;
        height: number;
    };
    backgroundStyle: CSSProperties;
    backgroundColor: string;
    currentBreakpoint: Breakpoint;
    breakpoints: { [P: string]: number };
    containerWidth: number;
    editLayoutWidthStyle: string;
    layoutTotalRows: number;
    onWidthChange: (newWidth: number) => void;
    onDragStart: () => void;
    onDragStop: () => void;
    onCoordinatesHoverChange: (hasCoordinates: boolean) => void;
    isGrabbingWidget: boolean;
    onResizeStop: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onResize: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onBreakpointChange: (newBreakpoint: Breakpoint) => void;
    onLayoutChange: (currentLayout: GridLayoutElement[], allLayouts: GridLayouts) => void;
    onTextCoordinatesSelected: (coordinates: WidgetCoordinates) => void;
    widgetBeingResized?: WidgetBeingResized;
    gridLayoutsWithFrame: GridLayouts;
} => {
    const defaultLayoutType = (selectedLayoutParam: LayoutType): LayoutType => {
        if (selectedLayoutParam === LayoutType.A4) {
            return selectedLayoutParam;
        }
        if (isTablet) {
            return LayoutType.TABLET;
        }
        if (isDesktop || isSmartTV) {
            return LayoutType.DESKTOP;
        }
        return LayoutType.MOBILE;
    };
    const { currentUrl } = useNavigation();
    const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>(defaultLayoutType(selectedLayout));
    const [breakpoints, setBreakpoints] = useState<{ [P: string]: number }>(buildBreakpoint(defaultLayoutType(selectedLayout)));
    const [widgetBeingResized, setWidgetBeingResized] = useState<undefined | WidgetBeingResized>(undefined);
    const [isGridBackgroundVisible, setIsGridBackgroundVisible] = useState(false);
    const [isCoordinatesBackgroundVisible, setIsCoordinatesBackgroundVisible] = useState(false);
    const [isGrabbingWidget, setIsGrabbingWidget] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const [layoutTotalRows, setLayoutTotalRows] = useState(0);
    const [gridLayoutsWithFrame, setGridLayoutsWithFrame] = useState<GridLayouts>(allGridLayouts);
    const gridLayoutsWithFrameRef = useRef<GridLayouts>(allGridLayouts);
    const originalGridLayouts = gridLayouts;
    const [wrongSizeLayouts, setWrongSizeLayouts] = useState<LayoutType[]>([]);
    const { layouts } = useLayoutsView();
    const { setHasLayoutChanges, setLayouts, setAllGridLayouts } = dispatchLayoutsView();
    const { setWarningAlert, setSuccessAlert } = dispatchAlertToaster();
    const { show: showAlert, type: alertType } = useAlertToaster();
    const hasPendingA4LayoutChanges = useRef(false);
    const lastSyncedA4LayoutSignature = useRef<string | undefined>(undefined);
    const cellSize = useMemo(() => calculateCellSize(containerWidth, currentBreakpoint), [containerWidth, currentBreakpoint]);
    const shouldShowBackground = editingLayout || isGridBackgroundVisible || isCoordinatesBackgroundVisible;

    const backgroundStyle = useMemo(
        () =>
            generateGridBackground(shouldShowBackground, cellSize, [CELL_MARGIN, CELL_MARGIN], COLUMNS[currentBreakpoint], containerWidth),
        [shouldShowBackground, cellSize, containerWidth, currentBreakpoint]
    );

    const style = useMemo(
        () => ({
            background: backgroundStyle,
        }),
        [backgroundStyle]
    );

    const mergeLayoutElementMetadata = (layout: ReactGridLayout[]): GridLayoutElement[] => {
        const sourceLayout =
            selectedLayout === LayoutType.A4
                ? gridLayoutsWithFrameRef.current[LayoutType.A4] ?? allGridLayouts[selectedLayout]
                : allGridLayouts[selectedLayout];

        if (!sourceLayout) return [];
        const layoutElementsMap = sourceLayout.reduce((map, obj: GridLayoutElement) => {
            map.set(obj.i, obj);
            return map;
        }, new Map<string, GridLayoutElement>());
        return layout.map((reactGridLayoutElement: ReactGridLayout) => {
            const layoutElement = layoutElementsMap.get(reactGridLayoutElement.i);
            if (layoutElement) {
                return {
                    ...reactGridLayoutElement,
                    maxHeight: layoutElement.maxHeight,
                    type: layoutElement.type,
                    maxWidth: layoutElement.maxWidth,
                    minHeight: layoutElement.minHeight,
                    minWidth: layoutElement.minWidth,
                };
            }
            const gridLayoutElement = reactGridLayoutElement as Partial<GridLayoutElement>;
            return {
                ...reactGridLayoutElement,
                maxHeight: gridLayoutElement.maxHeight ?? 3,
                type: gridLayoutElement.type ?? LayoutElementType.WIDGET,
                maxWidth: gridLayoutElement.maxWidth ?? 3,
                minHeight: gridLayoutElement.minHeight ?? 3,
                minWidth: gridLayoutElement.minWidth ?? 3,
            };
        });
    };

    const handleLayoutChange = (_currentLayout: GridLayoutElement[], allLayouts: Record<string, ReactGridLayout[]>) => {
        if (!allLayouts[selectedLayout]?.length) return;

        const layoutWithMetadata = mergeLayoutElementMetadata(allLayouts[selectedLayout]);

        const nextLayout = selectedLayout === LayoutType.A4 ? withDashboardFrame(layoutWithMetadata) : layoutWithMetadata;

        if (editingLayout) {
            const updatedLayouts = {
                ...allGridLayouts,
                [selectedLayout]: nextLayout,
            };

            setAllGridLayouts(updatedLayouts);

            const newLayouts = new Map<LayoutType, Layout>(layouts);
            newLayouts.set(selectedLayout, mapGridElementsToLayout(nextLayout, selectedLayout));
            setLayouts(newLayouts);
        }

        if (selectedLayout === LayoutType.A4) {
            hasPendingA4LayoutChanges.current = true;
            const nextGridLayoutsWithFrame = {
                ...gridLayoutsWithFrameRef.current,
                [LayoutType.A4]: nextLayout,
            };
            gridLayoutsWithFrameRef.current = nextGridLayoutsWithFrame;
            setGridLayoutsWithFrame(nextGridLayoutsWithFrame);
        }

        const currentComparable = getComparableLayout(allLayouts[selectedLayout]);
        const originalComparable = getComparableLayout(originalGridLayouts[selectedLayout] ?? []);

        setHasLayoutChanges(!areGridLayoutsEqual(currentComparable, originalComparable));

        if (selectedLayout === LayoutType.A4) {
            setLayoutTotalRows(computeLayoutRows(nextLayout));
        }
    };

    useEffect(() => {
        if (selectedLayout === LayoutType.A4 && gridLayouts[selectedLayout]?.length > 0) {
            const incomingA4LayoutSignature = getGridLayoutSignature(gridLayouts[selectedLayout]);
            if (hasPendingA4LayoutChanges.current && incomingA4LayoutSignature === lastSyncedA4LayoutSignature.current) {
                return;
            }

            const layoutWithMetadata = mergeLayoutElementMetadata(gridLayouts[selectedLayout]);

            const framed = withDashboardFrame(layoutWithMetadata);

            setLayouts(new Map([[LayoutType.A4, asLayout(framed, LayoutType.A4)]]));

            setLayoutTotalRows(computeLayoutRows(framed));

            const nextGridLayoutsWithFrame = {
                ...gridLayoutsWithFrameRef.current,
                [LayoutType.A4]: framed,
            };
            gridLayoutsWithFrameRef.current = nextGridLayoutsWithFrame;
            setGridLayoutsWithFrame(nextGridLayoutsWithFrame);

            hasPendingA4LayoutChanges.current = false;
            lastSyncedA4LayoutSignature.current = incomingA4LayoutSignature;
        }
    }, [gridLayouts]);

    const editLayoutWidthStyle =
        (editingLayout && breakpoints && Object.keys(breakpoints).length === 1 && Object.keys(breakpoints)[0] !== LayoutType.DESKTOP) ||
        (Object.keys(breakpoints).length === 1 && Object.keys(breakpoints)[0] === LayoutType.A4)
            ? `${breakpoints[Object.keys(breakpoints)[0]]}px`
            : '100%';

    useEffect(() => {
        // First time, when the container width is going from 0 to the actual size
        if (selectedLayout === LayoutType.A4) {
            setBreakpoints({ [LayoutType.A4]: BREAKPOINTS.A4 });
            return;
        }
        if (containerWidth > 0) {
            let currentInitialBreakpoint = LayoutType.MOBILE;
            if (isEditEnabled) {
                currentInitialBreakpoint = selectedLayout;
                setBreakpoints(buildBreakpoint(selectedLayout));
            } else if (isTablet) {
                currentInitialBreakpoint = LayoutType.TABLET;
            } else if (isDesktop || isSmartTV) {
                currentInitialBreakpoint = calculateBreakpointDesktop(containerWidth);
                setBreakpoints(BREAKPOINTS);
            }

            setCurrentBreakpoint(currentInitialBreakpoint);
        }
    }, [containerWidth, isEditEnabled, selectedLayout]);

    useEffect(() => {
        if (!widgetBeingResized) {
            Object.keys(wrongSizeWidgets).forEach((layoutType) => {
                if (
                    Object.entries(wrongSizeWidgets[layoutType as LayoutType]).length > 0 &&
                    !wrongSizeLayouts.includes(layoutType as LayoutType)
                ) {
                    setWrongSizeLayouts((prevState) => [...prevState, layoutType as LayoutType]);
                } else if (
                    Object.entries(wrongSizeWidgets[layoutType as LayoutType]).length === 0 &&
                    wrongSizeLayouts.includes(layoutType as LayoutType)
                ) {
                    const wrongSizeLayoutsCopy = [...wrongSizeLayouts];
                    const index = wrongSizeLayoutsCopy.findIndex((wrongLayoutCopy) => wrongLayoutCopy === layoutType);
                    wrongSizeLayoutsCopy.splice(index, 1);
                    setWrongSizeLayouts(wrongSizeLayoutsCopy);
                }
            });
        }
    }, [wrongSizeWidgets, widgetBeingResized?.i]);

    useEffect(() => {
        if (currentUrl.includes('/edit')) {
            if (
                showAlert &&
                wrongSizeLayouts.length === 0 &&
                Object.keys(allGridLayouts).length > 0 &&
                alertType === ALERT_ACTION_TYPE.WARNING
            ) {
                setSuccessAlert(i18n.t('dashboards.edit.dashBoardSizeIssuesSolved'));
            }
            if (wrongSizeLayouts.length > 0) {
                setWarningAlert(
                    i18n.t('dashboards.edit.dashboardHasSizeIssues', {
                        layoutTypes: wrongSizeLayouts
                            .map((layoutType: LayoutType) => i18n.t(`dashboards.layouts.${layoutType.toLowerCase()}SaveFeedback`))
                            .toString()
                            .toLowerCase()
                            .replaceAll(',', ', '),
                    })
                );
            }
        }
    }, [currentUrl, wrongSizeLayouts, alertType, showAlert, allGridLayouts]);

    const onResize = (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => {
        setIsGridBackgroundVisible(true);
        setIsGrabbingWidget(true);
        const layoutElementsById = allGridLayouts[selectedLayout].reduce((map, obj: GridLayoutElement) => {
            map.set(obj.i, obj);
            return map;
        }, new Map<string, GridLayoutElement>());
        const foundElement = layoutElementsById.get(newItem.i);
        if (foundElement) {
            const constraints = {
                minHeight: foundElement.minHeight,
                minWidth: foundElement.minWidth,
                maxHeight: foundElement.maxHeight,
                maxWidth: foundElement.maxWidth,
            };
            const elementBeingResizedFits = elementFitsContraints({
                ...newItem,
                ...constraints,
                type: foundElement.type,
            });
            if (elementBeingResizedFits) {
                setWidgetBeingResized({
                    ...newItem,
                    ...constraints,
                    type: foundElement.type,
                    fitsInGrid: true,
                });
            } else {
                setWidgetBeingResized({
                    ...newItem,
                    ...constraints,
                    type: foundElement.type,
                    fitsInGrid: false,
                });
            }
        }
    };

    const onResizeStop = (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => {
        setIsGridBackgroundVisible(false);
        setIsGrabbingWidget(false);
        if (widgetBeingResized?.fitsInGrid === false) {
            const index = layout.findIndex((gridLayout) => gridLayout.i === newItem.i);
            // react-grid-layout keeps its internal state in sync only when this item is restored in place.
            // eslint-disable-next-line no-param-reassign
            layout[index] = oldItem;
        }
        setWidgetBeingResized(undefined);
    };

    const handleTextCoordinatesSelected = (coordinates: WidgetCoordinates) => {
        if (selectedLayout !== LayoutType.A4) return;

        const currentA4Layout =
            gridLayoutsWithFrameRef.current[LayoutType.A4] ?? allGridLayouts[LayoutType.A4] ?? gridLayouts[LayoutType.A4] ?? [];
        const creationCoordinates = getWidgetCreationCoordinates(currentA4Layout, NewWidgetType.RICH_TEXT, coordinates);
        const nextA4Layout = withDashboardFrame([...currentA4Layout, createLocalTextBlockGridElement(creationCoordinates)]);
        const updatedLayouts = {
            ...allGridLayouts,
            [LayoutType.A4]: nextA4Layout,
        };

        setAllGridLayouts(updatedLayouts);

        const newLayouts = new Map<LayoutType, Layout>(layouts);
        newLayouts.set(LayoutType.A4, mapGridElementsToLayout(nextA4Layout, LayoutType.A4));
        setLayouts(newLayouts);

        hasPendingA4LayoutChanges.current = true;
        const nextGridLayoutsWithFrame = {
            ...gridLayoutsWithFrameRef.current,
            [LayoutType.A4]: nextA4Layout,
        };
        gridLayoutsWithFrameRef.current = nextGridLayoutsWithFrame;
        setGridLayoutsWithFrame(nextGridLayoutsWithFrame);
        setLayoutTotalRows(computeLayoutRows(nextA4Layout));
        setHasLayoutChanges(true);
    };

    let backgroundColor: string;
    if (editingLayout) {
        backgroundColor = colors.gray200;
    } else if (!editingLayout && currentBreakpoint === LayoutType.A4) {
        backgroundColor = colors.white;
    } else {
        backgroundColor = colors.gray100;
    }

    return {
        cellSize,
        backgroundStyle: shouldShowBackground ? style : {},
        backgroundColor,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        editLayoutWidthStyle,
        layoutTotalRows,
        onDragStart: () => {
            setIsGridBackgroundVisible(true);
            setIsCoordinatesBackgroundVisible(false);
            setIsGrabbingWidget(true);
        },
        onDragStop: () => {
            setIsGridBackgroundVisible(false);
            setIsCoordinatesBackgroundVisible(false);
            setIsGrabbingWidget(false);
        },
        onCoordinatesHoverChange: setIsCoordinatesBackgroundVisible,
        isGrabbingWidget,
        onResize,
        onResizeStop,
        onWidthChange: setContainerWidth,
        onBreakpointChange: setCurrentBreakpoint,
        onLayoutChange: handleLayoutChange,
        onTextCoordinatesSelected: handleTextCoordinatesSelected,
        widgetBeingResized,
        gridLayoutsWithFrame,
    };
};

const asLayout = (gridLayout: GridLayoutElement[], layoutType: LayoutType): Layout =>
    new Layout(
        layoutType,
        gridLayout.map((gridElement) => asWidgetLayout(gridElement))
    );

const asWidgetLayout = (gridElement: GridLayoutElement): LayoutElement => ({
    id: gridElement.i,
    position: {
        x: gridElement.x,
        y: gridElement.y,
    },
    size: {
        width: gridElement.w,
        height: gridElement.h,
    },
    type: gridElement.type,
    constraints: {
        maxHeight: gridElement.maxHeight,
        maxWidth: gridElement.maxWidth,
        minWidth: gridElement.minWidth,
        minHeight: gridElement.minHeight,
    },
});

const getGridLayoutSignature = (gridLayout: GridLayoutElement[]): string =>
    gridLayout
        .map((element) => [
            element.i,
            element.x,
            element.y,
            element.w,
            element.h,
            element.type,
            element.minWidth,
            element.minHeight,
            element.maxWidth,
            element.maxHeight,
        ])
        .sort(([a], [b]) => String(a).localeCompare(String(b)))
        .map((elementParts) => elementParts.join(':'))
        .join('|');
